import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'

import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { OverflowMenu } from '@/assets/icons'
import type { ReactElement } from 'react'
import type { HeaderButtonProps, HeaderProps } from './types'
export type { HeaderMenuFooter } from './types'
import { HEADER_HEIGHT_PX, MOBILE_HEADER_HEIGHT_PX } from './constants'
import { DEVICE_TYPE, useHeaderDeviceType } from './hooks/useHeaderDeviceType'

export type { HeaderProps } from './types'

function HeaderButton({ trailingIcon, mobileIcon, label, variant, color = 'primary', ...props }: HeaderButtonProps) {
  const isMobile = useHeaderDeviceType() !== DEVICE_TYPE.DESKTOP

  if (mobileIcon == null) {
    return (
      <Button {...props} size="sm" variant={variant} color={color} trailingIcon={trailingIcon} className="min-w-0 shrink truncate whitespace-nowrap">
        {label}
      </Button>
    )
  }

  const mobileIconButtonProps = {
    ...props,
    size: 'sm' as const,
    icon: mobileIcon as ReactElement,
    'aria-label': label,
  }

  if (isMobile && variant === 'filled') {
    return <IconButton {...mobileIconButtonProps} variant="filled" />
  } else if (isMobile) {
    return <IconButton {...mobileIconButtonProps} variant="plain" color={color === 'primary' ? 'primary' : 'neutral'} />
  } else {
    return (
      <Button {...props} size="sm" variant={variant} color={color} trailingIcon={trailingIcon} className="min-w-0 shrink truncate whitespace-nowrap">
        {label}
      </Button>
    )
  }
}

/**
 * Header component with optional title and right-aligned button and/or menu.
 */
export function Header({
  title,
  button,
  menuItems,
  menuDisabled = false,
  menuAriaLabel = 'Menu options',
  borderBottom,
  titleSize = 'xl',
  hideButtonOnMobile = false,
  menuFooter,
  leadingContent,
}: HeaderProps) {
  const deviceType = useHeaderDeviceType()
  const isMobile = deviceType !== DEVICE_TYPE.DESKTOP
  const hasTitle = title != null && title !== ''
  const showButton = button != null && !(isMobile && hideButtonOnMobile)
  const hasActions = showButton || menuItems != null || leadingContent != null
  const hasMenu = menuItems != null && menuItems.length > 0

  return (
    <header
      data-ds-component="Header"
      data-ds-title-size={titleSize}
      className={clsx(
        'flex w-full flex-row items-center relative',
        isMobile ? 'pl-4 py-3' : 'pl-10 py-4', // padding-left and padding-y
        hasMenu ? (isMobile ? 'pr-2' : 'pr-5') : isMobile ? 'pr-4' : 'pr-10', // padding-right: less when menu present (icon button has its own padding)
        hasTitle ? 'gap-2 justify-between' : 'justify-end',
        isMobile ? 'bg-brand-neutral-100' : 'bg-brand-neutral-50'
      )}
      style={{ height: `${isMobile ? MOBILE_HEADER_HEIGHT_PX : HEADER_HEIGHT_PX}px` }}
    >
      {hasTitle && (
        <h1
          className={clsx(
            'min-w-0 truncate font-rethink-sans font-semibold text-brand-neutral-950 not-italic',
            titleSize === 'sm' ? 'text-sm leading-5 tracking-[-0.15px]' : 'text-xl leading-7 tracking-normal'
          )}
          title={title}
        >
          {title}
        </h1>
      )}
      {hasActions && (
        <div className="flex items-center gap-2">
          {leadingContent}
          {showButton && <HeaderButton {...button!} />}
          {/* Rendered outside DropdownMenu when disabled — Radix ignores disabled on the trigger with asChild */}
          {hasMenu && menuDisabled && <IconButton variant="plain" color="black" size="lg" icon={<OverflowMenu />} aria-label={menuAriaLabel} disabled />}
          {hasMenu && !menuDisabled && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <IconButton variant="plain" color="black" size="lg" icon={<OverflowMenu />} aria-label={menuAriaLabel} />
              </DropdownMenu.Trigger>
              <MenuContent align="end" sideOffset={4} className="min-w-[160px]">
                <MenuItemGroup>
                  {menuItems.map((item, i) => (
                    <MenuItem
                      key={`${item.label}-${i}`}
                      label={item.label}
                      icon={item.icon}
                      variant={item.destructive ? 'destructive' : 'neutral'}
                      onSelect={() => item.onSelect?.()}
                      disabled={item.disabled}
                    />
                  ))}
                </MenuItemGroup>
                {menuFooter && (
                  <>
                    <MenuSeparator />
                    <DropdownMenu.Item onSelect={menuFooter.onSelect} className="max-w-[280px] cursor-pointer p-4 focus:outline-none">
                      {menuFooter.content}
                    </DropdownMenu.Item>
                  </>
                )}
              </MenuContent>
            </DropdownMenu.Root>
          )}
        </div>
      )}
      {/* Bottom border line with offset */}
      {(isMobile || borderBottom) && (
        <div
          className={clsx(
            'absolute bottom-0 right-0 bg-brand-neutral-200',
            // The left offset is -10px to align with the pixel line in the side nav
            isMobile ? 'left-0 h-[0.5px]' : 'left-[-10px] h-px'
          )}
        />
      )}
    </header>
  )
}
